import { logger } from '@treneo/shared/logger';

export const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    logger.warn('Failed to get browser timezone:', error);
    return 'UTC';
  }
};

export const isTimezoneMismatch = (userTimezone?: string): boolean => {
  if (!userTimezone) {
    return false;
  }

  const browserTimezone = getBrowserTimezone();
  return userTimezone !== browserTimezone;
};

export const getTimezoneOffset = (timezone: string, date?: Date): number => {
  try {
    const targetDate = date ?? new Date();
    const utc = targetDate.getTime() + targetDate.getTimezoneOffset() * 60000;
    const targetTime = new Date(utc + getTimezoneOffsetMs(timezone, targetDate));
    return Math.round((targetTime.getTime() - utc) / (1000 * 60 * 60));
  } catch (error) {
    logger.warn('Failed to get timezone offset:', { timezone, date }, error);
    return 0;
  }
};

const getTimezoneOffsetMs = (timezone: string, date: Date): number => {
  try {
    const target = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    const utc = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    return target.getTime() - utc.getTime();
  } catch (error) {
    logger.warn('Failed to get timezone offset ms:', { timezone, date }, error);
    return 0;
  }
};

export const formatTimezoneOffset = (timezone: string, date?: Date): string => {
  try {
    const offset = getTimezoneOffset(timezone, date);
    const sign = offset >= 0 ? '+' : '-';
    const hours = Math.abs(offset);
    return `UTC${sign}${hours.toString().padStart(2, '0')}:00`;
  } catch (error) {
    logger.warn('Failed to format timezone offset:', { timezone, date }, error);
    return 'UTC+00:00';
  }
};

export const getTimezoneDisplayName = (timezone: string): string => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'long'
    });

    const parts = formatter.formatToParts(new Date());
    const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value;

    return timeZoneName ?? timezone;
  } catch (error) {
    logger.warn('Failed to get timezone display name:', { timezone }, error);
    return timezone;
  }
};

export const createTimezoneMismatchMessage = (
  userTimezone: string,
  browserTimezone?: string
): string => {
  const browser = browserTimezone ?? getBrowserTimezone();
  const userOffset = formatTimezoneOffset(userTimezone);
  const browserOffset = formatTimezoneOffset(browser);

  return `Times shown in your preferred timezone (${userTimezone} ${userOffset}). Your browser is set to ${browser} ${browserOffset}.`;
};
